import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, map } from 'rxjs';

import { StaffMember } from '../models/app.models';
import { environment } from '../../environments/environment';

interface StaffMemberApi {
  id: number;
  name: string;
  title: string;
  bio: string | null;
  imageId: number | null;
  displayOrder: number;
  isActive: boolean;
}

interface StaffMemberRequest {
  name: string;
  title: string;
  bio: string | null;
  imageId: number | null;
  displayOrder: number;
  isActive: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class StaffMemberService {
  private readonly apiUrl =
    environment.apiUrl + '/staffmembers';

  private readonly mediaUrl =
    environment.apiUrl + '/media';

  constructor(
    private readonly http: HttpClient
  ) {}

  /**
   * Public Team page.
   * Backend only returns active staff.
   */
  getStaffMembers(): Observable<StaffMember[]> {
    return this.http
      .get<StaffMemberApi[]>(this.apiUrl)
      .pipe(
        map(members =>
          members.map(member =>
            this.fromApi(member)
          )
        )
      );
  }

  /**
   * Admin page.
   * Includes active and inactive staff.
   */
  getAdminStaffMembers(): Observable<StaffMember[]> {
    return this.http
      .get<StaffMemberApi[]>(
        `${this.apiUrl}/admin`,
        {
          withCredentials: true
        }
      )
      .pipe(
        map(members =>
          members.map(member =>
            this.fromApi(member)
          )
        )
      );
  }

  createStaffMember(
    member: StaffMember
  ): Observable<StaffMember> {
    return this.http
      .post<StaffMemberApi>(
        this.apiUrl,
        this.toRequest(member),
        {
          withCredentials: true
        }
      )
      .pipe(
        map(savedMember =>
          this.fromApi(savedMember)
        )
      );
  }

  updateStaffMember(
    member: StaffMember
  ): Observable<void> {
    return this.http.put<void>(
      `${this.apiUrl}/${member.id}`,
      this.toRequest(member),
      {
        withCredentials: true
      }
    );
  }

  deleteStaffMember(
    id: number
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.apiUrl}/${id}`,
      {
        withCredentials: true
      }
    );
  }

  private fromApi(
    member: StaffMemberApi
  ): StaffMember {
    return {
      id: member.id,
      name: member.name,
      title: member.title,
      bio: member.bio ?? '',
      imageId: member.imageId,

      imageUrl: member.imageId
        ? `${this.mediaUrl}/${member.imageId}`
        : '',

      displayOrder: member.displayOrder,
      isActive: member.isActive
    };
  }

  private toRequest(
    member: StaffMember
  ): StaffMemberRequest {
    return {
      name: member.name.trim(),
      title: member.title.trim(),

      bio:
        member.bio.trim() || null,

      imageId: member.imageId,

      displayOrder:
        member.displayOrder,

      isActive:
        member.isActive
    };
  }
}